import React, { useEffect } from 'react';
import { Navigate, Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import { ProtoFileUploadExtension } from './scaffolder/ProtoFileUpload';
import { ServiceRepoAutocompleteExtension } from './scaffolder/ServiceRepoAutocomplete';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { AnnouncementsPage } from '@backstage-community/plugin-announcements';
import { PullRequestsPage } from './components/pullRequests/PullRequestsPage';
import { TeamAdminPage } from './components/teamAdmin/TeamAdminPage';
import { NetworkPolicyPage } from './components/networkPolicy/NetworkPolicyPage';
import { KnowledgeGraphPage } from './components/knowledgeGraph/KnowledgeGraphPage';
import { EnvDriftPage } from './components/envDrift/EnvDriftPage';
import { ServiceAccessPage } from './components/serviceAccess/ServiceAccessPage';
import { ControlPanelPage } from './components/controlPanel/ControlPanelPage';
import { ClaudeUsagePage } from './components/claudeMetrics/ClaudeUsagePage';
import { OAuthAuthorizePage } from './components/oauth/OAuthAuthorizePage';
import { SignalsDisplay } from '@backstage/plugin-signals';
import { RequestAccessOnDenied } from './components/access/RequestAccessOnDenied';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { M42ProviderSettings } from './components/auth/M42ProviderSettings';
import { M42SignInPage } from './components/auth/M42SignInPage';
import { entityPage } from './components/catalog/EntityPage';
import { McpCatalogPage } from './components/mcpCatalog/McpCatalogPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';
import { ViewAsProvider } from './components/viewAs/ViewAsContext';
import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { configApiRef, useApi } from '@backstage/core-plugin-api';

// Custom theme
import LightIcon from '@material-ui/icons/WbSunny';
import DarkIcon from '@material-ui/icons/Brightness2';
import CssBaseline from '@mui/material/CssBaseline';
import { UnifiedThemeProvider } from '@backstage/theme';
import { lightTheme, darkTheme } from './theme/customTheme';
import { FocusVisibleStyles } from './theme/FocusVisibleStyles';


// Home Page Plugin
import { HomepageCompositionRoot } from '@backstage/plugin-home';
import { HomePage } from './components/home/HomePage';

// Permission imports
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import {
  OnlineVisibilityPermission,
  ClaudeMetricsReadPermission,
} from '@internal/backstage-plugin-ocp-custom-permissions-backend';
import { BasicPermission } from '@backstage/plugin-permission-common';

// App setup, themes, sign in page, bound routes
const app = createApp({
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => {
      const configApi = useApi(configApiRef);
      const isDevelopment = configApi.getString('auth.environment') === 'development';
      if (isDevelopment) {
        return ( 
          <SignInPage
            {...props}
            auto
            providers={[
              'guest',
            ]}
          />
        );
      }
      // Microsoft only; Google is shown disabled (PLAT-3071). A browser that last used
      // Microsoft is restored silently.
      return <M42SignInPage {...props} />;
      },
  },
  themes: [{
    id: 'light-theme',
    title: 'Light',
    variant: 'light',
    icon: <LightIcon />,
    Provider: ({ children }) => (
      <UnifiedThemeProvider theme={lightTheme}>
        <CssBaseline />
        <FocusVisibleStyles />
        {children}
      </UnifiedThemeProvider>
    ),
  },
  {
    id: 'dark-theme',
    title: 'Dark',
    variant: 'dark',
    icon: <DarkIcon />,
    Provider: ({ children }) => (
      <UnifiedThemeProvider theme={darkTheme}>
        <CssBaseline />
        <FocusVisibleStyles />
        {children}
      </UnifiedThemeProvider>
    ),
  }]
});

// Permissions
// Setting up the route permissions/groups
const withPermission = (Component: React.ComponentType<any>, permissions: BasicPermission[]) => {
  return (props: any) => {
    return permissions.reduce((wrappedComponent, permission) => {
        return (
          <RequirePermission permission={permission}>
            {wrappedComponent}
          </RequirePermission>
      );
    }, <Component {...props} />);
  };
};

// Template options
interface Entity {
  metadata: {
    tags?: string[];
  };
}

const ocpScaffolderGroups = [
  {
    title: "Online | Services",
    filter: (entity: Entity) =>
      (entity?.metadata?.tags?.includes('online') && !entity?.metadata?.tags?.includes('create') && !entity?.metadata?.tags?.includes('experimental')) ?? false,
  },
  {
    title: "Online | New Resource Creation",
    filter: (entity: Entity) =>
      (entity?.metadata?.tags?.includes('online') && entity?.metadata?.tags?.includes('create') && !entity?.metadata?.tags?.includes('experimental')) ?? false,
  },
  {
    title: "Experimental | Do Not Use",
    filter: (entity: Entity) =>
      entity?.metadata?.tags?.includes('experimental') ?? false,
  }
]

const ocpScaffolderFilters = (entity: Entity) => !(entity?.metadata?.tags?.includes('internal') ?? false); // hide internal templates (e.g. write-catalog-info editor backend)

// Each TemplateGroup inside ScaffolderPage renders a ContentHeader, which sets
// a Helmet title. With react-helmet's innermost-wins rule the document title
// ends up being whichever non-empty group renders last (e.g. "Experimental |
// Do Not Use" in prod). Override via document.title and keep it stable for
// the lifetime of the page so async re-renders can't reclaim it.
//
// Must be rendered as a SIBLING of ScaffolderPage inside the Route element,
// not as a wrapper around it. Backstage's traverseElementTree walks fragments
// transparently but cannot see inside custom function components, so wrapping
// ScaffolderPage hides it from routable-extension discovery and breaks the
// /create route entirely. Sibling-in-fragment is fine. Throwaway shim, drop
// when upstream fixes TemplateGroup.
const CreatePageTitleEnforcer = () => {
  useEffect(() => {
    const base = 'Templates | Backstage - M42';
    // Preserve a leading "(N) " unread-count prefix that the notifications
    // plugin's useTitleCounter prepends; only correct the base portion.
    // Without this, the enforcer strips the count every 250ms and the plugin
    // re-adds it, making the tab title flip between "(1) Templates …" and
    // "Templates …". Mirror the plugin's exact prefix regex (/^\(\d+\)\s/).
    const enforce = () => {
      const prefix = document.title.match(/^\(\d+\)\s/)?.[0] ?? '';
      const desired = prefix + base;
      if (document.title !== desired) document.title = desired;
    };
    enforce();
    const id = window.setInterval(enforce, 250);
    return () => window.clearInterval(id);
  }, []);
  return null;
};

// Routes
const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomepageCompositionRoot />}>
      <HomePage />
    </Route>

    <Route path="/catalog" element={<CatalogIndexPage />}/>
    <Route path="/mcp-catalog" element={<McpCatalogPage />} />
    <Route path="/catalog/:namespace/:kind/:name" element={<CatalogEntityPage />}>
      {entityPage}
    </Route>

    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route path="/docs/:namespace/:kind/:name/*" element={<TechDocsReaderPage />}>
      <TechDocsAddons>
        <ReportIssue />
        <Mermaid config={{ theme: 'forest', themeVariables: { lineColor: '#000000' } }} />
      </TechDocsAddons>
    </Route>

    <Route path="/create" element={
      <>
        <ScaffolderPage groups={ocpScaffolderGroups} templateFilter={ocpScaffolderFilters} />
        <CreatePageTitleEnforcer />
      </>
    }>
      <ScaffolderFieldExtensions>
        <ProtoFileUploadExtension />
        <ServiceRepoAutocompleteExtension />
      </ScaffolderFieldExtensions>
    </Route>
            
    <Route path="/api-docs" element={withPermission(ApiExplorerPage, [OnlineVisibilityPermission])({})} />

    <Route path="/catalog-import" element={withPermission(CatalogImportPage, [catalogEntityCreatePermission])({})}/>

    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>

    <Route
      path="/settings"
      element={<UserSettingsPage providerSettings={<M42ProviderSettings />} />}
    />

    <Route path="/catalog-graph" element={withPermission(CatalogGraphPage, [OnlineVisibilityPermission])({})} />

    <Route path="/notifications" element={<NotificationsPage />} />

    <Route path="/announcements" element={<AnnouncementsPage />} />

    <Route path="/pull-requests" element={<PullRequestsPage />} />

    <Route path="/team-admin" element={<TeamAdminPage />} />

    {/* Legacy deep links keep working; the tabbed shell is the real home. */}
    <Route path="/network-policy" element={<NetworkPolicyPage />} />
    <Route path="/knowledge-graph" element={<KnowledgeGraphPage />} />
    <Route
      path="/service-access"
      element={<Navigate to="/service-access/network" replace />}
    />
    <Route path="/service-access/:tab" element={<ServiceAccessPage />} />

    <Route path="/control-panel" element={<ControlPanelPage />} />

    {/* Promoted out of the Control Panel tab: sidebar-findable for everyone
        the permission admits, denied identities get the standard error page. */}
    <Route
      path="/claude-usage"
      element={withPermission(ClaudeUsagePage, [ClaudeMetricsReadPermission])({})}
    />

    {/* Env Drift board (PLAT-1374): intended (git) vs actual (ArgoCD) per env. */}
    <Route path="/env-drift" element={<EnvDriftPage />} />

    {/* OAuth consent page for Backstage-as-OAuth-provider (PLAT-405): the auth backend
        redirects here after `authorize`; without this route the SPA 404s. */}
    <Route path="/oauth2/authorize/:sessionId" element={<OAuthAuthorizePage />} />

  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <SignalsDisplay />
    {/* P1: global request-access prompt on any permission denial. */}
    <RequestAccessOnDenied />
    <AppRouter>
      <ViewAsProvider>
        <Root>
          {routes}
        </Root>
      </ViewAsProvider>
    </AppRouter>
  </>,
);
